/**
 * Advertorial Sticky Bottom CTA
 *
 * Tracks the exact viewport position of CTA buttons that have `data-gh-sticky-cta`.
 *
 * Triggers (relative to the target button):
 * - center: Activates when button reaches or passes the vertical center of screen (rect.top <= windowHeight * 0.5)
 * - top: Activates when button scrolls past the top of the screen (rect.bottom <= 0)
 * - bottom: Activates when button enters the bottom of the screen (rect.top <= windowHeight)
 *
 * Position / Direction behaviors:
 * - down: Active when button has reached/passed trigger point (button is above). Hides when button is below trigger.
 * - up: Active when button is below trigger point (user is above button). Hides when button passes above trigger.
 * - always: Stays active after button reaches trigger point.
 */
(function () {
  'use strict';

  var stickyBar = null;
  var stickyLink = null;
  var activeSourceCta = null;
  var ticking = false;
  var isVisible = false;

  function createStickyBar() {
    if (stickyBar) return stickyBar;

    stickyBar = document.createElement('div');
    stickyBar.className = 'gh-sticky-cta-bar';
    stickyBar.setAttribute('aria-hidden', 'true');

    var inner = document.createElement('div');
    inner.className = 'gh-sticky-cta-bar__inner';

    stickyLink = document.createElement('a');
    stickyLink.className = 'gh-cta__link';
    inner.appendChild(stickyLink);

    stickyBar.appendChild(inner);
    document.body.appendChild(stickyBar);

    return stickyBar;
  }

  function syncStickyContent(sourceEl) {
    if (!sourceEl) return;
    createStickyBar();

    var sourceLink = sourceEl.querySelector('.gh-cta__link');
    if (!sourceLink || !stickyLink) return;

    stickyLink.textContent = sourceLink.textContent.trim();
    stickyLink.href = sourceLink.href || '#';

    if (sourceLink.target) {
      stickyLink.target = sourceLink.target;
    } else {
      stickyLink.removeAttribute('target');
    }

    if (sourceLink.rel) {
      stickyLink.rel = sourceLink.rel;
    } else {
      stickyLink.removeAttribute('rel');
    }

    // Inherit CSS custom properties from source button
    var bg = sourceEl.style.getPropertyValue('--gh-cta-bg');
    var color = sourceEl.style.getPropertyValue('--gh-cta-color');
    var radius = sourceEl.style.getPropertyValue('--gh-cta-radius');

    if (bg) stickyLink.style.setProperty('--gh-cta-bg', bg);
    if (color) stickyLink.style.setProperty('--gh-cta-color', color);
    if (radius) stickyLink.style.setProperty('--gh-cta-radius', radius);
  }

  function updateStickyState() {
    ticking = false;

    var stickyCtas = document.querySelectorAll('[data-gh-sticky-cta]');
    if (!stickyCtas.length) {
      if (stickyBar && isVisible) {
        stickyBar.classList.remove('is-visible');
        isVisible = false;
      }
      return;
    }

    var windowHeight = window.innerHeight || document.documentElement.clientHeight;
    var windowWidth = window.innerWidth || document.documentElement.clientWidth;

    var shouldShow = false;
    var matchedSource = null;

    for (var i = 0; i < stickyCtas.length; i++) {
      var cta = stickyCtas[i];
      var mobileOnly = cta.getAttribute('data-sticky-mobile-only') === 'true';

      // Skip if mobile-only is enabled and we are on desktop (viewport >= 750px)
      if (mobileOnly && windowWidth >= 750) {
        continue;
      }

      var triggerSetting = cta.getAttribute('data-sticky-trigger') || 'center';
      var directionSetting = cta.getAttribute('data-sticky-direction') || 'down';

      var rect = cta.getBoundingClientRect();

      // Determine the trigger threshold (Y position in viewport)
      var triggerThreshold = windowHeight * 0.5; // default: center
      if (triggerSetting === 'top' || triggerSetting === 'button_passed') {
        triggerThreshold = 0; // top of screen
      } else if (triggerSetting === 'bottom' || triggerSetting === 'enter') {
        triggerThreshold = windowHeight; // bottom of screen
      } else if (triggerSetting === 'center' || triggerSetting === 'middle' || triggerSetting === 'page_middle') {
        triggerThreshold = windowHeight * 0.5; // center of screen
      }

      // Check whether button has reached or passed the trigger point (moving up past threshold)
      var isButtonAboveTrigger = false;
      if (triggerSetting === 'top' || triggerSetting === 'button_passed') {
        isButtonAboveTrigger = rect.bottom <= 0;
      } else {
        isButtonAboveTrigger = rect.top <= triggerThreshold;
      }

      var isButtonBelowTrigger = !isButtonAboveTrigger;

      // Evaluate display condition based on direction setting:
      if (directionSetting === 'down') {
        // Show only when button is above trigger (user scrolled down past button/trigger)
        // Hide when button is below trigger (user is above button)
        if (isButtonAboveTrigger) {
          shouldShow = true;
          matchedSource = cta;
          break;
        }
      } else if (directionSetting === 'up') {
        // Show when button is below trigger (user is above button)
        // Hide when button is above trigger (user scrolled down past button)
        if (isButtonBelowTrigger) {
          shouldShow = true;
          matchedSource = cta;
          break;
        }
      } else if (directionSetting === 'always') {
        // Always show once button has reached or passed trigger
        if (isButtonAboveTrigger) {
          shouldShow = true;
          matchedSource = cta;
          break;
        }
      }
    }

    if (matchedSource) {
      if (matchedSource !== activeSourceCta) {
        activeSourceCta = matchedSource;
      }
      syncStickyContent(activeSourceCta);
    }

    if (shouldShow && !isVisible) {
      if (!stickyBar) createStickyBar();
      syncStickyContent(activeSourceCta);
      stickyBar.classList.add('is-visible');
      isVisible = true;
    } else if (!shouldShow && isVisible) {
      if (stickyBar) {
        stickyBar.classList.remove('is-visible');
      }
      isVisible = false;
    }
  }

  function onScroll() {
    if (!ticking) {
      window.requestAnimationFrame(updateStickyState);
      ticking = true;
    }
  }

  function init() {
    var stickyCtas = document.querySelectorAll('[data-gh-sticky-cta]');
    if (stickyCtas.length > 0) {
      activeSourceCta = stickyCtas[0];
      createStickyBar();
      syncStickyContent(activeSourceCta);
    }
    window.removeEventListener('scroll', onScroll);
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    updateStickyState();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Theme customizer live update listeners
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:select', init);
  document.addEventListener('shopify:block:select', init);
  document.addEventListener('shopify:block:deselect', init);
})();
